import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    IconButton,
    Button,
    TextField,
    InputAdornment,
    useTheme,
    useMediaQuery,
    Chip,
    // Backdrop,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ContactForm from '../components/ContactForm';
import ContactList from '../components/ContactList';
import { getBusinesses, createContact, getContacts, updateContact, deleteContact, getBranches, grantAppAccess, revokeAppAccess } from '../api';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { useSelection } from '../context/SelectionContext';
import { dedupeBusinesses, deriveBusinessDisplayName } from '../utils/business';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import Tooltip from '@mui/material/Tooltip';
import { useLocation, useNavigate } from 'react-router-dom';

let contactsPageCache = null;

const ContactsPage = () => {
    // Get role information from the Auth Context
    const { isAdmin, userBusinessId, user, userContactId } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const userId = user?.uid || 'anonymous';
    const cachedState = contactsPageCache && contactsPageCache.userId === userId ? contactsPageCache : null;
    const cacheMatchesContext = Boolean(
        cachedState && cachedState.isAdmin === isAdmin && cachedState.userBusinessId === userBusinessId,
    );

    // State management
    const [businesses, setBusinesses] = useState(cachedState?.businesses || []);
    const [branches, setBranches] = useState(cachedState?.branches || []);
    const [contacts, setContacts] = useState(cachedState?.contacts || []);
    const {
        selectedBusinessId,
        setSelectedBusinessId,
        selectedBusinessName,
        clearBranch,
        setSelectedBranchId,
    } = useSelection();
    const [loading, setLoading] = useState(
        cachedState ? { businesses: false, contacts: false } : { businesses: true, contacts: false },
    );
    const [error, setError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [isCreateFormVisible, setIsCreateFormVisible] = useState(false);
    const { showNotification } = useNotification();
    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', confirmLabel: 'Confirm', confirmColor: 'primary', onConfirm: null });
    const [searchQuery, setSearchQuery] = useState(cachedState?.searchQuery || '');
    const [contactsBusinessId, setContactsBusinessId] = useState(cachedState?.contactsBusinessId || '');
    const [branchFilterId, setBranchFilterId] = useState(cachedState?.branchFilterId || '');
    const [branchFilterLabel, setBranchFilterLabel] = useState(cachedState?.branchFilterLabel || '');
    const location = useLocation();
    const navigate = useNavigate();
    const [grantingContactId, setGrantingContactId] = useState('');

    const resolveBusinessName = useCallback(
        (businessId) => {
            const match = businesses.find(b => b.businessId === businessId);
            return deriveBusinessDisplayName(match);
        },
        [businesses],
    );



    // Effect to fetch necessary business data based on role
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const skipLoading = cacheMatchesContext && businesses.length;
                if (!skipLoading) {
                    setLoading(p => ({ ...p, businesses: true }));
                }
                const response = await getBusinesses();
                const activeBusinesses = (response.data || []).filter(b => (b.status || 'ACTIVE') !== 'INACTIVE');
                const uniqueBusinesses = dedupeBusinesses(activeBusinesses);
                setBusinesses(uniqueBusinesses);
                if (selectedBusinessId && !selectedBusinessName) {
                    const matched = uniqueBusinesses.find(b => b.businessId === selectedBusinessId);
                    if (matched) {
                        setSelectedBusinessId(matched.businessId, deriveBusinessDisplayName(matched));
                    }
                }
                if (!isAdmin && userBusinessId) {
                    const matchingBusiness = uniqueBusinesses.find(b => b.businessId === userBusinessId);
                    if (matchingBusiness) {
                        setSelectedBusinessId(matchingBusiness.businessId, deriveBusinessDisplayName(matchingBusiness));
                    } else {
                        setSelectedBusinessId('');
                    }
                } else if (uniqueBusinesses.length > 0) {
                    const exists = uniqueBusinesses.some(b => b.businessId === selectedBusinessId);
                    if (!selectedBusinessId || !exists) {
                        const fallbackBusiness = uniqueBusinesses[0];
                        setSelectedBusinessId(fallbackBusiness.businessId, deriveBusinessDisplayName(fallbackBusiness));
                    }
                } else {
                    setSelectedBusinessId('');
                }
            } catch (err) {
                console.error("Failed to fetch businesses for dropdown", err);
                setError("Could not load businesses. Please refresh the page.");
            } finally {
                setLoading(p => ({ ...p, businesses: false }));
            }
        };
        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, userBusinessId]); // Re-run if role changes

    const fetchContactsForBusiness = async (businessId, { force = false } = {}) => {
        if (!businessId) {
            setContacts([]);
            setBranches([]);
            setContactsBusinessId('');
            if (branchFilterId) {
                setBranchFilterId('');
                setBranchFilterLabel('');
                clearBranch();
            }
            setLoading(p => ({ ...p, contacts: false }));
            return;
        }
        try {
            setError('');
            const skipLoading = !force && cacheMatchesContext && contactsBusinessId === businessId && contacts.length;
            if (!skipLoading) {
                setLoading(p => ({ ...p, contacts: true }));
            }
            const response = await getContacts(businessId);
            setContacts(response.data);
            const branchResponse = await getBranches(businessId);
            const activeBranches = (branchResponse.data || []).filter(branch => (branch.status || 'ACTIVE') !== 'INACTIVE');
            setBranches(activeBranches);
            setContactsBusinessId(businessId);
        } catch (err) {
            console.error(`Could not load contacts for business ${businessId}`, err);
            setError(`Could not load contacts for the selected business.`);
        } finally {
            setLoading(p => ({ ...p, contacts: false }));
        }
    };

    useEffect(() => {
        fetchContactsForBusiness(selectedBusinessId);
        setSearchQuery('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBusinessId]);

    useEffect(() => {
        if (!location.state || !location.state.branchId) return;
        const { branchId, branchLabel, businessId, businessName } = location.state;
        if (businessId && businessId !== selectedBusinessId) {
            setSelectedBusinessId(businessId, businessName || resolveBusinessName(businessId));
        }
        setBranchFilterId(branchId);
        setBranchFilterLabel(branchLabel || '');
        setSelectedBranchId(branchId);
        navigate(location.pathname, { replace: true });
    }, [
        location.state,
        location.pathname,
        navigate,
        selectedBusinessId,
        setSelectedBusinessId,
        setSelectedBranchId,
    ]);

    // --- CRUD Handlers ---

    const openConfirmDialog = ({ title, message, confirmLabel = 'Confirm', confirmColor = 'primary', onConfirm }) => {
        setConfirmDialog({ open: true, title, message, confirmLabel, confirmColor, onConfirm });
    };

    const closeConfirmDialog = () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
    };

    const handleConfirmDialog = async () => {
        const action = confirmDialog.onConfirm;
        closeConfirmDialog();
        if (typeof action === 'function') {
            await action();
        }
    };

    const handleFormSubmit = async (formData, contactId) => {
        try {
            if (contactId) { // Updating
                await updateContact(contactId, formData);
                showNotification({ message: 'Contact updated successfully.', severity: 'success' });
            } else { // Creating
                await createContact(formData);
                showNotification({ message: `Successfully created contact: ${formData.name}`, severity: 'success' });
                setIsCreateFormVisible(false);
            }
            await fetchContactsForBusiness(formData.businessId, { force: true });
            setIsModalOpen(false);
        } catch (err) {
            console.error("Failed to save contact:", err);
            if (err?.response?.status === 403) {
                showNotification({ message: err.response.data?.message || 'You do not have permission to update this contact.', severity: 'error', autoHideDuration: 6000 });
            } else {
                showNotification({ message: 'Could not save the contact. Please try again.', severity: 'error' });
            }
        }
    };

    const handleOpenUpdateModal = (contact) => {
        setEditingContact(contact);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setEditingContact(null);
        setIsModalOpen(false);
    };

    const deleteContactAndRefresh = async (contact) => {
        try {
            const businessId = contact.PK.replace('BUSINESS#', '');
            await deleteContact(contact.contactId, businessId);
            showNotification({ message: 'Contact deleted successfully.', severity: 'success' });
            await fetchContactsForBusiness(selectedBusinessId || businessId, { force: true });
        } catch (err) {
            console.error("Failed to delete contact:", err);
            showNotification({ message: 'Could not delete contact.', severity: 'error' });
        }
    };

    const handleDeleteClick = (contact) => {
        openConfirmDialog({
            title: 'Delete Contact',
            message: `Are you sure you want to delete ${contact.contactName}? This action cannot be undone.`,
            confirmLabel: 'Delete',
            confirmColor: 'error',
            onConfirm: () => deleteContactAndRefresh(contact),
        });
    };

    const grantAccessAndRefresh = async (contact) => {
        try {
            const businessId = (contact.PK && contact.PK.replace('BUSINESS#', ''))
                || contact.businessId
                || selectedBusinessId;
            if (!businessId) {
                showNotification({ message: 'Unable to determine business for this contact.', severity: 'warning' });
                return;
            }
            setGrantingContactId(contact.contactId);
            await grantAppAccess(contact.contactId, businessId);
            showNotification({ message: 'App access granted successfully. A password reset link has been generated.', severity: 'info', autoHideDuration: 6000 });
            await fetchContactsForBusiness(selectedBusinessId || businessId, { force: true });
        } catch (err) {
            console.error("Failed to grant app access:", err);
            showNotification({ message: 'Could not grant app access. Please try again.', severity: 'error' });
        } finally {
            setGrantingContactId('');
        }
    };

    const handleGrantAccessClick = (contact) => {
        openConfirmDialog({
            title: 'Grant App Access',
            message: `A sign-in link will be emailed to ${contact.contactName}. Proceed with granting portal access?`,
            confirmLabel: 'Send Invite',
            confirmColor: 'primary',
            onConfirm: () => grantAccessAndRefresh(contact),
        });
    };

    const handleRevokeAccessClick = (contact) => {
        if (!contact.firebaseUid) return;
        openConfirmDialog({
            title: 'Revoke App Access',
            message: `This will immediately remove ${contact.contactName}'s ability to sign in. Do you want to continue?`,
            confirmLabel: 'Revoke Access',
            confirmColor: 'error',
            onConfirm: async () => {
                try {
                    const businessId = contact.PK.replace('BUSINESS#', '');
                    await revokeAppAccess(contact.contactId, businessId);
                    showNotification({ message: 'App access revoked successfully.', severity: 'info' });
                    await fetchContactsForBusiness(selectedBusinessId || businessId, { force: true });
                } catch (err) {
                    console.error('Failed to revoke app access:', err);
                    showNotification({ message: 'Could not revoke app access. Please try again.', severity: 'error' });
                }
            },
        });
    };

    const handleClearBranchFilter = useCallback(() => {
        setBranchFilterId('');
        setBranchFilterLabel('');
        clearBranch();
    }, [clearBranch]);

    useEffect(() => {
        if (!branchFilterId) return;
        if (contactsBusinessId !== selectedBusinessId) return;
        if (!branches.length) {
            setBranchFilterId('');
            setBranchFilterLabel('');
            clearBranch();
            return;
        }
        const activeBranch = branches.find(branch => branch.branchId === branchFilterId);
        if (!activeBranch) {
            setBranchFilterId('');
            setBranchFilterLabel('');
            clearBranch();
            return;
        }
        const derivedLabel = activeBranch.nickname || activeBranch.address || activeBranch.branchId;
        if (derivedLabel !== branchFilterLabel) {
            setBranchFilterLabel(derivedLabel);
        }
    }, [
        branchFilterId,
        branches,
        branchFilterLabel,
        clearBranch,
        contactsBusinessId,
        selectedBusinessId,
    ]);

    useEffect(() => {
        if (!user) {
            contactsPageCache = null;
        }
    }, [user]);

    useEffect(() => {
        if (!user) return;
        contactsPageCache = {
            userId,
            isAdmin,
            userBusinessId,
            businesses,
            branches,
            contacts,
            contactsBusinessId,
            selectedBusinessId,
            branchFilterId,
            branchFilterLabel,
            searchQuery,
            timestamp: Date.now(),
        };
    }, [
        user,
        userId,
        isAdmin,
        userBusinessId,
        businesses,
        branches,
        contacts,
        contactsBusinessId,
        selectedBusinessId,
        branchFilterId,
        branchFilterLabel,
        searchQuery,
    ]);

    const filteredContacts = useMemo(() => {
        let workingContacts = contacts;
        if (branchFilterId) {
            const activeBranch = branches.find(branch => branch.branchId === branchFilterId);
            if (activeBranch) {
                const supervisorIds = activeBranch.supervisorIds || [];
                workingContacts = workingContacts.filter(contact => supervisorIds.includes(contact.contactId));
            } else {
                workingContacts = [];
            }
        }
        const term = searchQuery.trim().toLowerCase();
        if (!term) return workingContacts;
        return workingContacts.filter(contact => {
            const valuesToCheck = [
                contact.contactName,
                contact.email,
                contact.phoneNumber,
                contact.role,
            ];
            return valuesToCheck.some(value => typeof value === 'string' && value.toLowerCase().includes(term));
        });
    }, [contacts, searchQuery, branchFilterId, branches]);

    // const pageLoading = loading.businesses || loading.contacts;

    return (
        <Box>
            {/* <Backdrop
                open={pageLoading}
                sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
            >
                <CircularProgress color="inherit" />
            </Backdrop> */}
            {/* <Typography variant="h1" gutterBottom>Contact Personnel</Typography> */}
            <Stack spacing={3}>
                {isCreateFormVisible && (
                    <Paper elevation={isMobile ? 1 : 3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                        <Typography variant="h2" gutterBottom>Create New Contact</Typography>
                        <ContactForm
                            onSubmit={handleFormSubmit}
                            businesses={businesses}
                            currentUserContactId={userContactId}
                            onCancel={() => setIsCreateFormVisible(false)}
                        />
                    </Paper>
                )}

                <Paper elevation={isMobile ? 0 : 3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, bgcolor: isMobile ? 'background.paper' : 'background.default' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
                        {/* <Typography variant="h2">Existing Contacts</Typography> */}
                        <Stack direction="row" spacing={2} alignItems="center">
                            {!isCreateFormVisible && (
                                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreateFormVisible(true)} disabled={loading.businesses}>
                                    Add New Contact
                                </Button>
                            )}
                        </Stack>
                        <TextField
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search contacts"
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>

                    {branchFilterId && (
                        <Chip
                            label={`Filtered by branch: ${branchFilterLabel || 'Selected Branch'}`}
                            onDelete={handleClearBranchFilter}
                            color="primary"
                            variant="outlined"
                            sx={{ alignSelf: 'flex-start', mb: 2 }}
                        />
                    )}

                    {/* RBAC UI CHANGE: Show dropdown only for Admins */}
                    {isAdmin && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel id="select-business-label">Select a Business to View Contacts</InputLabel>
                                <Select
                                    labelId="select-business-label"
                                    value={selectedBusinessId}
                                    label="Select a Business to View Contacts"
                                    onChange={(e) => setSelectedBusinessId(e.target.value, resolveBusinessName(e.target.value))}
                                    disabled={loading.businesses}
                                >
                                    {businesses.map(b => (
                                        <MenuItem key={b.businessId} value={b.businessId}>
                                            {deriveBusinessDisplayName(b)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Tooltip title="Clear business filter">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setSelectedBusinessId('');
                                            clearBranch();
                                        }}
                                        disabled={!selectedBusinessId || loading.businesses}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                    {loading.contacts ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                    ) : (
                        <ContactList
                            contacts={filteredContacts}
                            branches={branches}
                            selectedBusinessId={selectedBusinessId}
                            onUpdateClick={handleOpenUpdateModal}
                            onDeleteClick={handleDeleteClick}
                            onGrantAccessClick={handleGrantAccessClick}
                            onRevokeAccessClick={handleRevokeAccessClick}
                            grantingContactId={grantingContactId}
                        />
                    )}
                </Paper>
            </Stack>

            <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <DialogTitle>
                    Edit Contact
                    <IconButton onClick={handleCloseModal} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent>
                    <ContactForm
                        onSubmit={handleFormSubmit}
                        businesses={businesses}
                        initialData={editingContact}
                        onCancel={handleCloseModal}
                        currentUserContactId={userContactId}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={confirmDialog.open} onClose={closeConfirmDialog} maxWidth="xs" fullWidth>
                <DialogTitle>{confirmDialog.title}</DialogTitle>
                {confirmDialog.message && (
                    <DialogContent>
                        <DialogContentText>{confirmDialog.message}</DialogContentText>
                    </DialogContent>
                )}
                <DialogActions>
                    <Button onClick={closeConfirmDialog} color="inherit">Cancel</Button>
                    <Button onClick={handleConfirmDialog} color={confirmDialog.confirmColor} variant="contained">
                        {confirmDialog.confirmLabel}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ContactsPage;
